import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid, Select, MenuItem, ListItemText, Checkbox, SelectChangeEvent } from '@mui/material';
import { ClassNameMap } from '@mui/styles';
import { createPmStaff, updatePmStaff } from '../sagas/accountManagementSlice';
import CancelIcon from '../../../icons/cancelIcon.svg';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { CompleteCompanyStaffDto, CreateCompleteCompanyStaffDto, UpdateCompleteCompanyStaffDto } from '@inosio/payments-sdk';
import { string, InferType, object, array, number } from 'yup';
import { useFormik } from 'formik';
import { InputField } from '../../../components/InputController';
import RolesDialog from './rolesDialog';
import { formatStringToSentenceCase } from '../../../shared/util/formaters';
import { EmailRegex } from '../../../utils/regex';

interface payloadArguments {
  isOpen: boolean;
  setIsOpen(isOpen: boolean): void;
  classes: ClassNameMap;
  EditUser?: CompleteCompanyStaffDto;
}

const schemaValidationPmStaff = object({
  first_name: string().required('Required First Name'),
  last_name: string().required('Required Last Name'),
  staff_email: string().matches(EmailRegex, 'Please type a valid Email.').required('Required'),
  selected_role_id: number().min(1).max(4).required(),
  property_group_uuids: array(string().required()),
});

type schemaValidationPmStaffType = InferType<typeof schemaValidationPmStaff>;

export default function BaseDialog(props: payloadArguments) {
  const dispatch = useDispatch();
  const [isRoleModalOpen, setIsRoleModalOpen] = useState<boolean>(false);
  const { propertyGroups } = useSelector(selectPropertyGroupDetailsState);

  const staffInitialValues: schemaValidationPmStaffType = {
    first_name: props.EditUser?.FirstName || '',
    last_name: props.EditUser?.LastName || '',
    staff_email: props.EditUser?.Email || '',
    property_group_uuids: props.EditUser?.AssignedPropertyGroupUuids || [],
    selected_role_id: props.EditUser?.CompanyStaffTypeIds[0] || 0,
  };

  const formikValidator = useFormik<schemaValidationPmStaffType>({
    initialValues: staffInitialValues,
    validationSchema: schemaValidationPmStaff,
    enableReinitialize: true,
    isInitialValid: false,
    onSubmit: async (values) => {
      return;
    },
  });

  useEffect(() => {
    if (!props.isOpen) {
      formikValidator.setValues(staffInitialValues);
    }

  }, [props.isOpen]);

  const handleChangePropGroupFilter = (event: SelectChangeEvent<typeof formikValidator.values.property_group_uuids>) => {
    const {
      target: { value },
    } = event;
    formikValidator.setFieldValue('property_group_uuids', value);
  };

  const getRoleName = (typeId: number) => {
    switch (typeId) {
      case 1:
        return 'Company Admin';
      case 2:
        return 'Property Manager';
      case 3:
        return 'Maintenance Coordinator';
      case 4:
        return 'Maintenance Staff';
      default:
        return null;
    }
  };

  const submitForm = () => {
    if (props.EditUser) {
      //edit mode
      const editStaffPayload: UpdateCompleteCompanyStaffDto = {
        FirstName: formikValidator.values.first_name,
        LastName: formikValidator.values.last_name,
        InvitationEmail: formikValidator.values.staff_email,
        CompanyStaffTypeIds: [formikValidator.values.selected_role_id],
        AssignedPropertyGroupUuids: formikValidator.values.property_group_uuids,
      };

      dispatch({
        type: updatePmStaff.type,
        payload: { data: editStaffPayload, uuid: props.EditUser.Uuid },
      });
    } else {
      //new mode
      const newStaffPayload: CreateCompleteCompanyStaffDto = {
        FirstName: formikValidator.values.first_name,
        LastName: formikValidator.values.last_name,
        InvitationEmail: formikValidator.values.staff_email,
        CompanyStaffTypeIds: [formikValidator.values.selected_role_id],
        AssignedPropertyGroupUuids: formikValidator.values.property_group_uuids,
      };

      dispatch({
        type: createPmStaff.type,
        payload: newStaffPayload,
      });
    }

    props.setIsOpen(false);
  };

  return (
    <Fragment>
      <Dialog
        classes={{ paper: props.classes.dialogPaper }}
        open={props.isOpen}
        onClose={() => {}}
        className={props.classes.createDialogBox}
        sx={{
          '& form.MuiBox-root': {
            height: 200,
            width: 365,
            mt: 2,
          },
        }}
        PaperProps={{ style: { borderRadius: 24 } }}
      >
        <DialogTitle sx={{ alignSelf: 'center', fontSize: 16, pt: '27px', justifyContent: 'flex-end', width: '100%', display: 'flex', '& p': { mt: 0, mb: 0 } }}>
          <p>{props.EditUser ? 'Edit User' : 'Create User'}</p>
          <IconButton
            sx={{ marginLeft: '140px', p: 0 }}
            onClick={() => {
              props.setIsOpen(false);
            }}
          >
            <img src={CancelIcon} alt="cancel icon" width="26px" height="26px" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ '& .MuiTextField-root': { mt: 3 }, '& .MuiButton-root': { mt: 4 } }}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={6}>
                <InputField name="first_name" label="First Name" isError={false} errors={formikValidator.errors?.first_name} touched={formikValidator.touched?.first_name} onBlur={formikValidator.handleBlur} onChange={(e) => formikValidator.setFieldValue('first_name', e.target.value)} value={formikValidator.values.first_name ? formatStringToSentenceCase(formikValidator.values.first_name) : ''} />
              </Grid>
              <Grid item xs={6}>
                <InputField name="last_name" label="Last Name" isError={false} errors={formikValidator.errors?.last_name} touched={formikValidator.touched?.last_name} onBlur={formikValidator.handleBlur} onChange={(e) => formikValidator.setFieldValue('last_name', e.target.value)} value={formikValidator.values.last_name ? formatStringToSentenceCase(formikValidator.values.last_name) : ''} />
              </Grid>
              <Grid item xs={12}>
                <InputField name="email" label="Email" isError={false} errors={formikValidator.errors?.staff_email} touched={formikValidator.touched?.staff_email} onBlur={formikValidator.handleBlur} onChange={(e) => formikValidator.setFieldValue('staff_email', e.target.value)} value={formikValidator.values.staff_email ?? ''} />
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="accountManagement"
                  onClick={() => {
                    setIsRoleModalOpen(true);
                  }}
                  sx={{ height: 49 }}
                >
                  {!formikValidator.values.selected_role_id ? 'Choose Role' : getRoleName(formikValidator.values.selected_role_id)}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Select
                  multiple
                  displayEmpty
                  value={formikValidator.values.property_group_uuids}
                  onChange={handleChangePropGroupFilter}
                  renderValue={(uuids) => {
                    if (uuids.length === 0) {
                      return 'Select Property Group(s)';
                    }
                    const names = propertyGroups.filter((pg) => uuids.includes(pg.Uuid)).map((pg) => pg.Description);
                    return names.join(', ');
                  }}
                  inputProps={{ 'aria-label': 'Without label' }}
                  style={{ borderRadius: '24px', height: '42px', width: '100%' }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '24px',
                      },
                    },
                  }}
                >
                  {propertyGroups.map((pg) => (
                    <MenuItem key={pg.Uuid} value={pg.Uuid}>
                      <Checkbox checked={!!formikValidator.values.property_group_uuids && formikValidator.values.property_group_uuids.indexOf(pg.Uuid) > -1} />
                      <ListItemText primary={pg.Description} />
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions className={props.classes.dialogActionButtons}>
          <Grid item display="flex" justifyContent="end" sx={{ width: '100%' }} mr={5.5}>
            <Button
              sx={{ textTransform: 'none', fontWeight: 400, fontFamily: 'Work Sans', fontSize: '16px' }}
              variant="contained"
              onClick={() => {
                submitForm();
              }}
              disabled={!formikValidator.isValid}
            >
              {props.EditUser ? 'Save' : 'Add User'}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
      <RolesDialog
        classes={props.classes}
        isOpen={isRoleModalOpen}
        initialRoleId={formikValidator.values.selected_role_id}
        selectRole={(roleId: number) => {
          formikValidator.setFieldValue('selected_role_id', roleId);
          setIsRoleModalOpen(false);
        }}
        dismiss={() => setIsRoleModalOpen(false)}
      />
    </Fragment>
  );
}
